import { useEffect, useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import { scrollToId } from "@/lib/site";

const PAINS = [
  "Scrivi. E aspetti giorni per una risposta.",
  "A fine mese non sai quanto hai guadagnato davvero.",
  "Hai firmato un contratto e non ricordi nemmeno come uscirne.",
  "I prezzi cambiano, e nessuno ti spiega perché.",
  "Più guadagni, più paghi. Il servizio resta uguale.",
];

const PCT = { booking: 0.23, pm: 0.3, cedolare: 0.21, variabili: 0.1 };
const YU_FISSO = 250;
const UPLIFTS = [0, 10, 20, 30];

const fmt = (n) => `${Math.round(n).toLocaleString("it-IT")}€`;
const fmtSigned = (n) => `−${fmt(Math.abs(n))}`;

export default function Ledger() {
  const [incasso, setIncasso] = useState(3000);
  const [uplift, setUplift] = useState(0);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incParam = params.get("incasso");
    const upParam = params.get("uplift");
    let shared = false;
    if (incParam !== null) {
      const inc = Number(incParam);
      if (inc >= 500 && inc <= 20000) {
        setIncasso(Math.round(inc / 100) * 100);
        shared = true;
      }
    }
    if (upParam !== null) {
      const up = Number(upParam);
      if (UPLIFTS.includes(up)) {
        setUplift(up);
        shared = true;
      }
    }
    if (shared) setTimeout(() => scrollToId("il-conto"), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pmNet = incasso * (1 - PCT.booking - PCT.pm - PCT.cedolare - PCT.variabili);
  const yuBase = incasso * (1 + uplift / 100);
  const yuNet = yuBase * (1 - PCT.booking - PCT.cedolare - PCT.variabili) - YU_FISSO;
  const delta = yuNet - pmNet;

  const share = async () => {
    const url = `${window.location.origin}?incasso=${incasso}&uplift=${uplift}#il-conto`;
    const text = `Su un incasso di ${fmt(incasso)} al mese, col property manager ti restano ${fmt(pmNet)}. Con YieldUp ${fmt(yuNet)}${uplift > 0 ? ` (vendite +${uplift}%)` : ""}. Fai il conto coi tuoi numeri:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "YU YieldUp — Fai tu il conto", text, url });
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Testo e link copiati — incollali dove vuoi.");
      } catch {
        toast.error("Copia non riuscita. Riprova.");
      }
    }
  };

  const ROWS = [
    { voce: "Booking (23%)", pm: incasso * PCT.booking, yu: yuBase * PCT.booking },
    { voce: "Property manager (30%)", pm: incasso * PCT.pm, yu: null },
    { voce: "Cedolare secca (21%)", pm: incasso * PCT.cedolare, yu: yuBase * PCT.cedolare },
    { voce: "Costi variabili (10%)", pm: incasso * PCT.variabili, yu: yuBase * PCT.variabili },
    { voce: "YieldUp (costo fisso)", pm: null, yu: YU_FISSO },
  ];

  return (
    <section id="il-conto" data-testid="ledger-section" className="border-b border-[#2A241F] py-12 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="section-kicker">Se ti hanno già deluso</p>
          <h2 className="mt-4 max-w-4xl font-heading text-3xl font-black uppercase leading-[1.05] tracking-tight text-[#FAF7F2] sm:text-4xl lg:text-5xl">
            Il tuo property manager ti ha deluso?
          </h2>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
            Non sei l'unico. Succede spesso: paghi tanto e a fine mese capisci poco. Se ti riconosci in queste frasi, il conto qui sotto ti interessa.
          </p>
        </Reveal>

        <div className="mt-12 max-w-3xl">
          {PAINS.map((pain, i) => (
            <Reveal key={pain} delay={i * 0.06}>
              <p data-testid={`pain-phrase-${i + 1}`} className={`${i === 2 || i === 3 ? "hidden sm:flex" : "flex"} items-baseline gap-5 border-t border-[#2A241F] py-3 text-base sm:py-5 font-light text-[#C4BBB0] last:border-b sm:text-lg`}>
                <span className="shrink-0 font-heading font-black text-[#E05A2C]">—</span>
                {pain}
              </p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 max-w-3xl text-base font-light leading-relaxed text-[#C4BBB0] sm:hidden">
            Chi lavora a percentuale vuole prenotazioni veloci, a qualsiasi prezzo. Io prendo un costo fisso: il mio interesse è farti guadagnare di più.
          </p>
          <p className="mt-16 hidden max-w-3xl text-base font-light leading-relaxed text-[#C4BBB0] sm:block sm:text-lg">
            C'è una differenza che non si vede dai numeri: chi lavora a percentuale vuole prenotazioni veloci, a qualsiasi prezzo — prima affitta, prima incassa, e se il prezzo era basso pazienza. Io prendo un costo fisso, qualunque cosa succeda: il mio interesse è identico al tuo, farti guadagnare di più.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <h3 className="mt-12 font-heading text-xl font-black uppercase tracking-tight text-[#FAF7F2] sm:text-2xl">
            Fai tu il conto, coi tuoi numeri
          </h3>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest text-[#A89F91]">Muovi il cursore: la ricevuta si ricalcola da sola</p>
        </Reveal>

        <Reveal delay={0.12} className="mt-5 sm:mt-8">
          <div className="mx-auto max-w-3xl border border-[#2A241F] bg-[#16110E] p-4 sm:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <label htmlFor="ledger-revenue-slider" className="section-kicker">
                Il tuo incasso mensile lordo
              </label>
              <span data-testid="ledger-revenue-value" className="font-heading text-3xl font-black tracking-tighter text-[#FAF7F2] sm:text-4xl">
                {fmt(incasso)}
              </span>
            </div>
            <input
              id="ledger-revenue-slider"
              data-testid="ledger-revenue-slider"
              type="range"
              min="500"
              max="20000"
              step="100"
              value={incasso}
              onChange={(e) => setIncasso(Number(e.target.value))}
              className="yu-slider mt-3 w-full sm:mt-5"
            />
            <div className="mt-2 flex justify-between font-mono text-xs uppercase tracking-widest text-[#A89F91]">
              <span>500€</span>
              <span>20.000€</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-5 sm:mt-8">
          <div data-testid="accounting-ledger-panel" className="mx-auto max-w-3xl -rotate-1 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.65)]">
            <div className="receipt-edge receipt-edge-top" />
            <div className="bg-[#F7F4EC] px-5 py-5 text-[#12100E] sm:px-12 sm:py-10">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-[#12100E] pb-2 sm:pb-4">
                <p className="font-mono text-xs font-semibold uppercase tracking-widest">Ricevuta · Conto del mese</p>
                <p className="font-mono text-xs uppercase tracking-widest text-[#686054]">
                  Incasso: {fmt(incasso)}{uplift > 0 ? ` · con YU vendite +${uplift}%` : ""}
                </p>
              </div>

              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-x-4 border-b border-[#DCD7C9] py-2 font-mono text-xs uppercase tracking-widest text-[#686054] sm:py-4 sm:text-xs">
                <span>Voce</span>
                <span className="text-right">Property manager</span>
                <span className="text-right">YieldUp{uplift > 0 ? ` (+${uplift}%)` : ""}</span>
              </div>

              {ROWS.map((row) => (
                <div key={row.voce} className="grid grid-cols-[1.7fr_1fr_1fr] gap-x-3 border-b border-[#DCD7C9] py-2 sm:grid-cols-[1.4fr_1fr_1fr] sm:gap-x-4 sm:py-4">
                  <span className="text-sm sm:text-base">{row.voce}</span>
                  <span className="text-right font-mono text-sm font-semibold text-[#B91C1C] sm:text-base">
                    {row.pm === null ? "—" : fmtSigned(-row.pm)}
                  </span>
                  <span className="text-right font-mono text-sm font-semibold text-[#B91C1C] sm:text-base">
                    {row.yu === null ? "—" : fmtSigned(-row.yu)}
                  </span>
                </div>
              ))}

              <div className="grid grid-cols-[1.4fr_1fr_1fr] items-baseline gap-x-4 py-4 sm:py-7">
                <span className="font-heading text-base font-black uppercase tracking-tight sm:text-xl">Ti resta</span>
                <span data-testid="ledger-pm-total" className="text-right font-mono text-base font-semibold text-[#686054] line-through decoration-[#12100E]/40 sm:text-xl">{fmt(pmNet)}</span>
                <span data-testid="ledger-yu-total" className="text-right font-heading text-4xl font-black tracking-tighter text-[#E05A2C] sm:text-6xl">{fmt(yuNet)}</span>
              </div>

              <div className="border-t-2 border-dashed border-[#12100E]/30 pt-4 text-center sm:pt-6">
                <p className="font-mono text-xs uppercase tracking-widest text-[#686054] sm:text-xs">La differenza</p>
                <p data-testid="ledger-delta" className="mt-2 font-heading text-2xl font-black uppercase tracking-tight sm:text-3xl">
                  {delta >= 0 ? `+${fmt(delta)} al mese, tuoi` : `${fmt(delta)} al mese`}
                </p>
                <p className="mt-2 text-sm text-[#52525B] sm:text-base">
                  Su un anno fanno {fmt(delta * 12)}. Che restano a te.
                </p>
              </div>

              <div className="receipt-barcode mt-5 sm:mt-8" />
              <button
                type="button"
                data-testid="ledger-how-toggle"
                aria-expanded={howOpen}
                onClick={() => setHowOpen((v) => !v)}
                className="mx-auto mt-3 flex min-h-[44px] items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#686054] underline decoration-[#DCD7C9] underline-offset-4 sm:hidden"
              >
                Come è calcolato {howOpen ? "−" : "+"}
              </button>
              <div className={`${howOpen ? "block" : "hidden"} sm:block`}>
                <p className="pt-2 text-center font-mono text-xs uppercase tracking-widest text-[#686054] sm:pt-4 sm:text-xs">
                  Stima semplificata con percentuali medie di mercato. Il tuo conto vero lo facciamo insieme, sui tuoi numeri.
                </p>
                <p className="pt-2 text-center font-mono text-xs uppercase tracking-widest text-[#686054] sm:text-xs">
                  Pulizie e check-in esclusi in entrambi i casi
                </p>
              </div>
            </div>
            <div className="receipt-edge" />
          </div>
        </Reveal>

        <Reveal delay={0.18} className="mt-10 hidden sm:block">
          <div className="mx-auto max-w-3xl text-center">
            <p className="section-kicker">E non è finita: lavoro per farti vendere di più</p>
            <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
              Prova a vedere cosa succede se le tue vendite salgono. Il costo fisso resta fermo: più vendi, più la differenza cresce.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {UPLIFTS.map((u) => (
                <button
                  key={u}
                  data-testid={`ledger-uplift-${u}`}
                  onClick={() => setUplift(u)}
                  className={`rounded-full border px-6 py-3.5 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
                    uplift === u
                      ? "border-[#E05A2C] bg-[#E05A2C] text-white"
                      : "border-[#383029] text-[#A89F91] hover:border-[#E05A2C] hover:text-[#FAF7F2]"
                  }`}
                >
                  {u === 0 ? "Vendite di oggi" : `+${u}% vendite`}
                </button>
              ))}
            </div>
            <button
              data-testid="ledger-share-button"
              onClick={share}
              className="mx-auto mt-8 flex min-h-[44px] items-center gap-2.5 rounded-full border border-[#383029] px-6 text-sm font-medium text-[#A89F91] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#E05A2C]"
            >
              <Share2 className="h-4 w-4" />
              Condividi questo conto
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.25} className="mt-8 flex flex-col gap-3 text-center sm:mt-12 sm:block sm:text-left">
          <button
            data-testid="ledger-share-button-mobile"
            onClick={share}
            className="flex min-h-[48px] w-full items-center justify-center gap-2.5 rounded-full border border-[#383029] px-6 text-sm font-medium text-[#C4BBB0] sm:hidden"
          >
            <Share2 className="h-4 w-4" />
            Condividi questo conto
          </button>
          <button
            data-testid="ledger-cta"
            onClick={() => scrollToId("chi-sono")}
            className="btn-glow min-h-[48px] w-full rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98] sm:w-auto"
          >
            Scopri chi segue la tua struttura
          </button>
        </Reveal>
      </div>
    </section>
  );
}
